import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, TrendingUp, Users, Award, Sparkles, ArrowRight, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const NAVY = '#0F172A';

const BENEFITS = [
  { icon: Users, title: 'Qualified leads', body: 'People actively looking for expert guidance across every wellness dimension.' },
  { icon: TrendingUp, title: 'Grow your practice', body: 'Expand your reach beyond your local network — India-wide visibility.' },
  { icon: Award, title: 'Build trust', body: 'Get verified and listed alongside India\'s most respected wellness professionals.' },
  { icon: Sparkles, title: 'India\'s wellness ecosystem', body: 'Be part of a platform that guides people from insight → action → progress.' },
];

const INITIAL_FORM = {
  full_name: '', profession: '', specialization: '', qualification: '',
  registration_number: '', years_of_experience: '', city: '',
  consultation_mode: [], languages: '', whatsapp: '', email: '',
  website: '', profile_photo_url: '', certificate_urls: '', short_bio: '',
};

function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

const inputCls = 'w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition';

export default function BecomePartner() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleMode = (m) => {
    setForm((f) => ({
      ...f,
      consultation_mode: f.consultation_mode.includes(m)
        ? f.consultation_mode.filter((x) => x !== m)
        : [...f.consultation_mode, m],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_of_experience: parseInt(form.years_of_experience, 10) || 0,
        languages: form.languages.split(',').map((s) => s.trim()).filter(Boolean),
        certificate_urls: form.certificate_urls.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
      };
      const res = await axios.post(`${API}/partners/apply`, payload);
      setDone(true);
      // Open WhatsApp with the pre-filled application summary
      if (res.data?.whatsapp_redirect_url) {
        window.open(res.data.whatsapp_redirect_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="partner-application-success">
        <div className="max-w-md w-full bg-white rounded-3xl p-10 text-center border border-slate-200 shadow-lg">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#4F46E5 0%,#9333EA 100%)' }}>
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: NAVY }}>Application received</h2>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            Thanks for applying. Our team will review your profile and reach out via WhatsApp or email at
            <strong className="text-slate-800"> clinityindia@gmail.com </strong>
            within 3–5 business days.
          </p>
          <p className="text-xs text-slate-400">A pre-filled WhatsApp message with your application summary has been opened in a new tab.</p>
          <a href="/" className="mt-6 inline-block text-sm font-semibold text-indigo-600 no-underline hover:underline">← Back to homepage</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ color: NAVY, fontFamily: 'Inter, sans-serif' }} data-testid="become-partner-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 backdrop-blur-xl" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <a href="/" className="text-lg font-bold no-underline" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>Clinity</a>
          <a href="/experts" className="text-sm font-semibold text-slate-600 hover:text-indigo-600 no-underline">Find Experts →</a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-14 pb-8 text-center">
        <div className="text-[11px] font-bold uppercase tracking-[0.28em] mb-4 text-indigo-600">Become a Clinity Partner</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5" style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.035em' }}>
          Help People Live Better.<br />Grow Your Practice With Clinity.
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Join Clinity's trusted network of wellness professionals and connect with people actively looking for expert guidance.
        </p>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 mb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-white rounded-2xl p-5 border border-slate-200" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.03)' }}>
              <div className="w-10 h-10 rounded-xl mb-3 flex items-center justify-center" style={{ background: '#EEF2FF' }}>
                <b.icon className="w-5 h-5" style={{ color: '#4F46E5' }} />
              </div>
              <div className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.title}</div>
              <div className="text-xs text-slate-500 leading-relaxed">{b.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-24">
        <form onSubmit={submit} className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200" style={{ boxShadow: '0 20px 40px -20px rgba(15,23,42,0.08)' }} data-testid="partner-application-form">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Partner Application</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name" required>
              <input required data-testid="pf-full-name" className={inputCls} value={form.full_name} onChange={setField('full_name')} placeholder="Your full name" />
            </Field>
            <Field label="Profession" required>
              <input required data-testid="pf-profession" className={inputCls} value={form.profession} onChange={setField('profession')} placeholder="Clinical Psychologist" />
            </Field>
            <Field label="Specialization" required>
              <input required data-testid="pf-specialization" className={inputCls} value={form.specialization} onChange={setField('specialization')} placeholder="Anxiety, burnout, CBT" />
            </Field>
            <Field label="Qualification" required>
              <input required data-testid="pf-qualification" className={inputCls} value={form.qualification} onChange={setField('qualification')} placeholder="M.Phil Clinical Psychology" />
            </Field>
            <Field label="Registration Number" hint="Council/board reg. number, if applicable">
              <input data-testid="pf-reg-number" className={inputCls} value={form.registration_number} onChange={setField('registration_number')} />
            </Field>
            <Field label="Years of Experience" required>
              <input required type="number" min="0" data-testid="pf-experience" className={inputCls} value={form.years_of_experience} onChange={setField('years_of_experience')} />
            </Field>
            <Field label="City" required>
              <input required data-testid="pf-city" className={inputCls} value={form.city} onChange={setField('city')} placeholder="Mumbai" />
            </Field>
            <Field label="Languages" required hint="Comma-separated, e.g. English, Hindi, Marathi">
              <input required data-testid="pf-languages" className={inputCls} value={form.languages} onChange={setField('languages')} />
            </Field>
            <Field label="WhatsApp" required>
              <input required data-testid="pf-whatsapp" className={inputCls} value={form.whatsapp} onChange={setField('whatsapp')} placeholder="+91 98XXX XXXXX" />
            </Field>
            <Field label="Email" required>
              <input required type="email" data-testid="pf-email" className={inputCls} value={form.email} onChange={setField('email')} />
            </Field>
            <Field label="Website / LinkedIn">
              <input data-testid="pf-website" className={inputCls} value={form.website} onChange={setField('website')} placeholder="https://…" />
            </Field>
            <Field label="Consultation Mode" required>
              <div className="flex gap-2">
                {['Online', 'In-person'].map((m) => {
                  const active = form.consultation_mode.includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleMode(m)} data-testid={`pf-mode-${m}`}
                      className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Profile Photo URL" hint="Public URL (Cloudinary / Google Drive share link)">
              <input data-testid="pf-photo" className={inputCls} value={form.profile_photo_url} onChange={setField('profile_photo_url')} placeholder="https://…" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Certificate URLs" hint="Paste one URL per line, or comma-separated">
                <textarea data-testid="pf-certs" className={inputCls} rows={3} value={form.certificate_urls} onChange={setField('certificate_urls')} placeholder="https://drive.google.com/…" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Short Bio (150–300 chars)" required>
                <textarea required data-testid="pf-bio" className={inputCls} rows={4} value={form.short_bio} onChange={setField('short_bio')} maxLength={2000} />
              </Field>
            </div>
          </div>

          {error && <div className="mt-5 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="pf-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            data-testid="pf-submit"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-base transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg,#4F46E5 0%,#7C3AED 60%,#9333EA 100%)',
              boxShadow: '0 18px 40px -12px rgba(79,70,229,0.55)',
            }}
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Apply to Become a Clinity Partner <ArrowRight className="w-4 h-4" /></>}
          </button>

          <p className="text-xs text-slate-400 mt-4 text-center">By applying, you'll be reviewed by our team and notified via WhatsApp/email within 3–5 business days.</p>
        </form>
      </section>
    </div>
  );
}
